import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { UserRole } from '../types';
import { RefreshCw } from 'lucide-react';

interface SocialAuthButtonsProps {
  mode?: 'signin' | 'signup';
  selectedRole?: UserRole;
  redirectUrl?: string;
  onSuccess?: (user: { name: string; email: string; role: string }) => void;
  onError?: (error: string) => void;
  layout?: 'grid' | 'stacked' | 'pills';
  showLabel?: boolean;
}

export const SocialAuthButtons: React.FC<SocialAuthButtonsProps> = ({
  mode = 'signin',
  selectedRole = 'Buyer',
  redirectUrl,
  onSuccess,
  onError,
  layout = 'grid',
  showLabel = false,
}) => {
  const navigate = useNavigate();
  const { signInWithSocial } = useAuth();
  const { language } = useLanguage();
  const isFr = language === 'fr';

  const [activeProvider, setActiveProvider] = useState<string | null>(null);

  const handleSocialAuth = async (provider: 'google' | 'facebook' | 'apple' | 'microsoft' | 'twitter') => {
    setActiveProvider(provider);
    try {
      const res = await signInWithSocial(provider, selectedRole);
      setActiveProvider(null);

      if (res.success && res.user) {
        const userName =
          res.user.displayName ||
          (res.user.email && res.user.email.includes('@') ? res.user.email.split('@')[0] : '') ||
          'User';
        const userEmail = res.user.email || '';
        onSuccess?.({
          name: userName,
          email: userEmail,
          role: selectedRole,
        });

        // Navigate to target destination only if explicitly requested or if no custom callback handles it
        if (redirectUrl) {
          navigate(redirectUrl, { replace: true });
        } else if (!onSuccess) {
          navigate(isFr ? '/fr/mon-profil' : '/en/profile', { replace: true });
        }
      } else if (!res.success && res.error) {
        onError?.(res.error);
      }
    } catch (err: any) {
      setActiveProvider(null);
      onError?.(err?.message || (isFr ? 'Erreur de connexion sociale' : 'Social authentication error'));
    }
  };

  // Official Provider Icons
  const GoogleIcon = () => (
    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
      />
    </svg>
  );

  const FacebookIcon = () => (
    <svg className="w-4 h-4 shrink-0 text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );

  const AppleIcon = () => (
    <svg className="w-4 h-4 shrink-0 fill-current text-slate-900" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.63-.77 1.06-1.84.94-2.91-.91.04-2.02.61-2.67 1.38-.58.68-1.09 1.77-.95 2.82 1.02.08 2.05-.52 2.68-1.29z" />
    </svg>
  );

  const MicrosoftIcon = () => (
    <svg className="w-4 h-4 shrink-0" viewBox="0 0 23 23" aria-hidden="true">
      <path fill="#f35325" d="M1 1h10v10H1z" />
      <path fill="#81bc06" d="M12 1h10v10H12z" />
      <path fill="#05a6f0" d="M1 12h10v10H1z" />
      <path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  );

  if (layout === 'stacked') {
    return (
      <div className="space-y-2.5 w-full">
        {/* Google / Gmail */}
        <button
          type="button"
          onClick={() => handleSocialAuth('google')}
          disabled={Boolean(activeProvider)}
          className="w-full py-2.5 px-4 bg-white hover:bg-slate-50 border border-slate-300 hover:border-slate-400 rounded-lg flex items-center justify-center gap-3 text-xs font-medium text-slate-700 transition-all shadow-2xs hover:shadow-xs disabled:opacity-60 cursor-pointer"
        >
          {activeProvider === 'google' ? (
            <RefreshCw className="w-4 h-4 animate-spin text-slate-600" />
          ) : (
            <GoogleIcon />
          )}
          <span>
            {mode === 'signup'
              ? isFr
                ? 'Créer un compte avec Google (Gmail)'
                : 'Sign up with Google (Gmail)'
              : isFr
              ? 'Continuer avec Google (Gmail)'
              : 'Continue with Google (Gmail)'}
          </span>
        </button>

        {/* Facebook */}
        <button
          type="button"
          onClick={() => handleSocialAuth('facebook')}
          disabled={Boolean(activeProvider)}
          className="w-full py-2.5 px-4 bg-[#1877F2] hover:bg-[#166fe5] text-white rounded-lg flex items-center justify-center gap-3 text-xs font-medium transition-all shadow-2xs hover:shadow-xs disabled:opacity-60 cursor-pointer"
        >
          {activeProvider === 'facebook' ? (
            <RefreshCw className="w-4 h-4 animate-spin text-white" />
          ) : (
            <svg className="w-4 h-4 shrink-0 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          )}
          <span>
            {mode === 'signup'
              ? isFr
                ? 'Créer un compte avec Facebook'
                : 'Sign up with Facebook'
              : isFr
              ? 'Continuer avec Facebook'
              : 'Continue with Facebook'}
          </span>
        </button>

        {/* Apple & Microsoft row */}
        <div className="grid grid-cols-2 gap-2 pt-0.5">
          <button
            type="button"
            onClick={() => handleSocialAuth('apple')}
            disabled={Boolean(activeProvider)}
            className="py-2.5 px-3 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg flex items-center justify-center gap-2 text-xs font-medium text-slate-800 transition-all shadow-2xs disabled:opacity-60 cursor-pointer"
          >
            {activeProvider === 'apple' ? (
              <RefreshCw className="w-4 h-4 animate-spin text-slate-600" />
            ) : (
              <AppleIcon />
            )}
            <span>Apple</span>
          </button>

          <button
            type="button"
            onClick={() => handleSocialAuth('microsoft')}
            disabled={Boolean(activeProvider)}
            className="py-2.5 px-3 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg flex items-center justify-center gap-2 text-xs font-medium text-slate-800 transition-all shadow-2xs disabled:opacity-60 cursor-pointer"
          >
            {activeProvider === 'microsoft' ? (
              <RefreshCw className="w-4 h-4 animate-spin text-slate-600" />
            ) : (
              <MicrosoftIcon />
            )}
            <span>Microsoft</span>
          </button>
        </div>
      </div>
    );
  }

  // Default Grid layout (4 social brand buttons)
  return (
    <div className="grid grid-cols-4 gap-2 w-full">
      {/* Google */}
      <button
        type="button"
        onClick={() => handleSocialAuth('google')}
        disabled={Boolean(activeProvider)}
        className="py-2.5 px-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg flex items-center justify-center transition-all shadow-2xs hover:shadow-xs group cursor-pointer disabled:opacity-60"
        title={isFr ? 'Se connecter avec Google (Gmail)' : 'Sign in with Google (Gmail)'}
      >
        {activeProvider === 'google' ? (
          <RefreshCw className="w-4 h-4 animate-spin text-slate-600" />
        ) : (
          <GoogleIcon />
        )}
      </button>

      {/* Facebook */}
      <button
        type="button"
        onClick={() => handleSocialAuth('facebook')}
        disabled={Boolean(activeProvider)}
        className="py-2.5 px-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg flex items-center justify-center transition-all shadow-2xs hover:shadow-xs group cursor-pointer disabled:opacity-60"
        title={isFr ? 'Se connecter avec Facebook' : 'Sign in with Facebook'}
      >
        {activeProvider === 'facebook' ? (
          <RefreshCw className="w-4 h-4 animate-spin text-[#1877F2]" />
        ) : (
          <FacebookIcon />
        )}
      </button>

      {/* Apple */}
      <button
        type="button"
        onClick={() => handleSocialAuth('apple')}
        disabled={Boolean(activeProvider)}
        className="py-2.5 px-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg flex items-center justify-center transition-all shadow-2xs hover:shadow-xs group cursor-pointer disabled:opacity-60"
        title={isFr ? 'Se connecter avec Apple' : 'Sign in with Apple'}
      >
        {activeProvider === 'apple' ? (
          <RefreshCw className="w-4 h-4 animate-spin text-slate-900" />
        ) : (
          <AppleIcon />
        )}
      </button>

      {/* Microsoft */}
      <button
        type="button"
        onClick={() => handleSocialAuth('microsoft')}
        disabled={Boolean(activeProvider)}
        className="py-2.5 px-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg flex items-center justify-center transition-all shadow-2xs hover:shadow-xs group cursor-pointer disabled:opacity-60"
        title={isFr ? 'Se connecter avec Microsoft' : 'Sign in with Microsoft'}
      >
        {activeProvider === 'microsoft' ? (
          <RefreshCw className="w-4 h-4 animate-spin text-slate-600" />
        ) : (
          <MicrosoftIcon />
        )}
      </button>
    </div>
  );
};
